import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import NavBar from "./chat/navbar";
import ChatScreen from "./chat/ChatScreen";
import AdminDataSourcesScreen from "./admin/DataSources/AdminDataSourcesScreen";
import AdminUserScreen from "./admin/Users/AdminUserScreen";
import axios from "axios";
import { useAgentConfig } from "./contexts/AgentConfigContext";
import { useAuth } from "./contexts/AuthContext";
import AgentSelector from "./components/AgentSelector";
import ConfirmationModal from "./common_jsx/ConfirmationModal";
import { parseJSONResponse, isJSONResponse, processJSONContent, extractUnknownTypeInsights } from "./utils/responseParser";
import { extractSqlTableData, processAgentExecutionAnswer } from "./utils/tableParser";
import { loadSessionModes, saveSessionMode } from "./utils/sessionModeUtils";
import Sidebar from "./components/Sidebar";
import IntegrationsModal from "./components/oauth/IntegrationsModal";
import SessionNotification from "./components/SessionNotification";

// Utility functions for feedback persistence
const FEEDBACK_STORAGE_KEY = 'message_feedback_cache';

const getFeedbackFromStorage = () => {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading feedback from storage:', error);
    return {};
  }
};

function MainContent(props) {
  const { config, isLoading: isConfigLoading, error: configError, securityParams } = useAgentConfig();
  const { getUserIdDiscover } = useAuth();

  // Session-completion notifications (replaces generic toast for stream-end events)
  const [sessionNotifications, setSessionNotifications] = useState([]);
  const [liveChat, setLiveChat] = useState([]);
  const [missingSessionParams, setMissingSessionParams] = useState([]);
  // General session-creation error (e.g. invalid param values). Shown in a red
  // banner instead of the "not set" amber one used for genuinely missing params.
  const [sessionCreationError, setSessionCreationError] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [activeChatSession, setActiveChatSession] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [selectedScreen, setSelectedScreen] = useState("ChatScreen");
  const [sessionModes, setSessionModes] = useState({});
  // Set of session IDs currently streaming (drives sidebar spinner badges)
  const [streamingSessions, setStreamingSessions] = useState(new Set());
  // Set of session IDs that finished streaming while the user was elsewhere (drives unread dots)
  const [unreadSessions, setUnreadSessions] = useState(new Set());

  // Pagination state for session list
  const SESSION_PAGE_SIZE = 30;
  const [sessionPage, setSessionPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);

  // Stable refs so callbacks created once always see the latest values
  const activeChatSessionRef = useRef(activeChatSession);
  useEffect(() => { activeChatSessionRef.current = activeChatSession; }, [activeChatSession]);
  const chatSessionsRef = useRef(chatSessions);
  useEffect(() => { chatSessionsRef.current = chatSessions; }, [chatSessions]);
  const getUserIdDiscoverRef = useRef(getUserIdDiscover);
  useEffect(() => { getUserIdDiscoverRef.current = getUserIdDiscover; }, [getUserIdDiscover]);

  // Called by ChatScreen when a session starts streaming
  const handleStreamStart = useCallback((sessionId) => {
    setStreamingSessions((prev) => {
      if (prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
  }, []);

  const removeSessionNotification = useCallback((id) => {
    setSessionNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Called by ChatScreen when a session finishes streaming
  const handleStreamEnd = useCallback((sessionId, userMessage) => {
    setStreamingSessions((prev) => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
    // Only mark as unread + notify when the user was viewing a different session
    if (sessionId !== activeChatSessionRef.current) {
      setUnreadSessions((prev) => {
        if (prev.has(sessionId)) return prev;
        const next = new Set(prev);
        next.add(sessionId);
        return next;
      });
      const notifId = `notif-${sessionId}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      setSessionNotifications((prev) => [
        ...prev,
        {
          id: notifId,
          userMessage: userMessage || 'Response ready',
          onClick: () => { if (chatSessionsRef.current?.some((s) => s.id === sessionId)) setActiveChatSession(sessionId); },
        },
      ]);
    }
  }, []);

  // Clear unread badge when user switches to that session
  useEffect(() => {
    if (activeChatSession) {
      setUnreadSessions((prev) => {
        if (!prev.has(activeChatSession)) return prev;
        const next = new Set(prev);
        next.delete(activeChatSession);
        return next;
      });
    }
  }, [activeChatSession]);

  // Util: Key for storing active session per agent
  const getActiveSessionStorageKey = (agentId) => `activeSessionId_${agentId || ''}`;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  //Sidebar show hide
  const [isSideBar, setIsSideBar] = useState(true);

  // Integrations modal state
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  // Ref to track newly created sessions to prevent race condition with message loading
  const newlyCreatedSessionsRef = useRef(new Set());

  // Ref to track sessions that are known to be empty (no user messages)
  // This persists across navigations and ensures welcome screen shows consistently
  const emptySessionsRef = useRef(new Set());

  // Navbar
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(1);


  const updateSessionTimestamp = (sessionId) => {
    // Skip if the session ID doesn't exist
    if (!sessionId) return;

    // Find the session to update
    const sessionToUpdate = chatSessions.find((s) => s.id === sessionId);
    if (!sessionToUpdate) return;

    const now = Date.now();

    // Update the timestamp only — do NOT call setActiveChatSession here.
    // A stale setTimeout with the old activeChatSession value would overwrite
    // the freshly set session ID (e.g. after creating a new session), causing
    // the message-loading effect to fire with null and clearing the chat.
    const updatedSessions = chatSessions.map((session) =>
      session.id === sessionId ? { ...session, update_time: now } : session
    );
    setChatSessions(updatedSessions);
  };

  const handleScroll = (event) => {
    setScrollDirection(event.currentTarget.scrollTop < scrollTop ? 1 : 0);
    setScrollTop(event.currentTarget.scrollTop);
  };

  const deleteSession = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      const agentId = config.agentId;
      const deleteResponse = await axios.delete(`${config.serviceUrl}/agents/${agentId}/sessions`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${config.apiKey}`,
        },
        data: {
          ids: [sessionToDelete],
          discover_user_id: getUserIdDiscover(),
        },
      });

      if (!deleteResponse.data || deleteResponse.data.code !== 0) {
        throw new Error(deleteResponse.data?.message || "Delete failed");
      }

      // Update local state by removing the deleted session
      setChatSessions((prevSessions) => prevSessions.filter((s) => s.id !== sessionToDelete));
      setTotalSessions((prev) => Math.max(0, prev - 1));

      // Remove deleted session from unread/streaming sets to avoid phantom IDs
      setUnreadSessions((prev) => { const next = new Set(prev); next.delete(sessionToDelete); return next; });
      setStreamingSessions((prev) => { const next = new Set(prev); next.delete(sessionToDelete); return next; });

      // If the deleted session was active, select another session or null
      if (activeChatSession === sessionToDelete) {
        if (chatSessions.length > 1) {
          // Find another session to make active (preferably the most recent one)
          const remainingSessions = chatSessions.filter((s) => s.id !== sessionToDelete);
          const sortedSessions = [...remainingSessions].sort((a, b) => b.update_time - a.update_time);
          setActiveChatSession(sortedSessions[0].id);
        } else {
          // No sessions left, clear active session
          setActiveChatSession(null);
          setLiveChat([]);
        }
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      alert("Failed to delete session: " + error.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSessionToDelete(null);
    }
  };

  const handleDeleteClick = (sessionId, e) => {
    // Stop the click from bubbling up to the session button
    e.stopPropagation();
    setSessionToDelete(sessionId);
    setShowDeleteModal(true);
  };

  const handleSessionRename = async (sessionId, newTitle) => {
    const session = chatSessions.find((s) => s.id === sessionId);
    if (!session) return;
    const oldTitle = session.summary;

    setChatSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, summary: newTitle } : s))
    );

    try {
      await axios.put(
        `${config.serviceUrl}/agents/${config.agentId}/sessions`,
        { session_id: sessionId, user_id: getUserIdDiscover(), title: newTitle },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${config.apiKey}` } }
      );
    } catch (error) {
      console.error("Error renaming session:", error);
      setChatSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, summary: oldTitle } : s))
      );
    }
  };

  const handleSessionPin = async (sessionId) => {
    const session = chatSessions.find((s) => s.id === sessionId);
    if (!session) return;
    const newPinnedState = !session.is_pinned;

    // Optimistic update
    setChatSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, is_pinned: newPinnedState } : s))
    );

    try {
      await axios.put(
        `${config.serviceUrl}/agents/${config.agentId}/sessions`,
        { session_id: sessionId, user_id: getUserIdDiscover(), is_pinned: newPinnedState },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${config.apiKey}` } }
      );
    } catch (error) {
      console.error("Error pinning session:", error);
      // Revert on failure
      setChatSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, is_pinned: !newPinnedState } : s))
      );
    }
  };

  // Load session modes from localStorage when agent changes
  useEffect(() => {
    if (config.agentId) {
      setSessionModes(loadSessionModes(config.agentId));
    }
  }, [config.agentId]);

  const handleSessionModeChange = (sessionId, mode) => {
    if (sessionId && mode) {
      setSessionModes((prev) => ({ ...prev, [sessionId]: mode }));
    }
  };

  // Clear the missing-params banner whenever the user updates security params
  useEffect(() => {
    setMissingSessionParams([]);
    setSessionCreationError(null);
  }, [securityParams]);

  // Reset session when agent changes
  useEffect(() => {
    setActiveChatSession(null);
    setLiveChat([]);
    setChatSessions([]);
    // CRITICAL FIX: Clear session tracking refs to prevent stale data from previous agent
    // These refs store session IDs marked as "empty" or "newly created" from the old agent
    // Without clearing them, the message-loading logic may incorrectly skip fetching messages
    // for sessions in the new agent, showing the welcome screen instead of actual messages
    emptySessionsRef.current.clear();
    newlyCreatedSessionsRef.current.clear();
    // Clear the missing-params banner so stale errors from the previous agent don't linger
    setMissingSessionParams([]);
    setSessionCreationError(null);
  }, [config.agentId]);

  // Persist active session ID to localStorage per agent
  useEffect(() => {
    if (activeChatSession && config.agentId) {
      try {
        localStorage.setItem(getActiveSessionStorageKey(config.agentId), activeChatSession);
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [activeChatSession, config.agentId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!config.agentId) return;
      setIsLoadingSessions(true);
      try {
        const agentId = config.agentId;
        const response = await axios.get(`${config.serviceUrl}/agents/${agentId}/sessions`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${config.apiKey}`,
          },
          params: {
            discover_user_id: getUserIdDiscover(),
            page: 1,
            page_size: SESSION_PAGE_SIZE,
          },
        });

        if (response.status === 200 && response.data && response.data.data) {
          const formattedSessions = response.data.data.map((session) => {
            let summary = session.title || "New Session";

            let source = "";

            // Handle both executed_queries (array) and executed_query (string)
            if (session.executed_queries && session.executed_queries.length > 0) {
              source = session.executed_queries[0].query;
            } else if (session.executed_query) {
              source = session.executed_query;
            }

            // Check if session has user messages
            // A session is considered "empty" if it has no messages or only assistant messages
            const hasUserMessages = session.messages && session.messages.length > 0 && session.messages.some(msg =>
              msg.role === "user"
            );

            return {
              id: session.id,
              author_id: session.role === "user" ? props.user.id : 0,
              summary: summary,
              update_time: session.update_time || Date.now(),
              source: source,
              message: session.content,
              type: session.content_type || "text",
              datatype: session.content_type === "table" ? "sql" : "text",
              description: session.description || "",
              hasUserMessages: hasUserMessages,
              is_pinned: session.is_pinned ?? false,
            };
          });
          setChatSessions(formattedSessions);
          setSessionPage(1);
          setTotalSessions(response.data.total ?? 0);
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      } finally {
        setIsLoadingSessions(false); // Set loading to false when done
      }
    };

    fetchData();
    // getUserIdDiscover is not wrapped in useCallback in AuthContext so its reference
    // changes on every AuthContext render. Including it would cause unnecessary re-runs;
    // the resolved ID only changes on actual auth state changes (login/logout), which
    // already trigger config re-fetches via other deps. Safe to exclude here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.agentId, config.serviceUrl, config.apiKey, props.user.id]);

  const loadMoreSessions = useCallback(async () => {
    if (isLoadingMoreSessions || chatSessions.length >= totalSessions) return;
    setIsLoadingMoreSessions(true);
    const nextPage = sessionPage + 1;
    try {
      const response = await axios.get(`${config.serviceUrl}/agents/${config.agentId}/sessions`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${config.apiKey}`,
        },
        params: {
          discover_user_id: getUserIdDiscoverRef.current(),
          page: nextPage,
          page_size: SESSION_PAGE_SIZE,
        },
      });
      if (response.status === 200 && response.data?.data) {
        const newItems = response.data.data.map((session) => {
          let summary = session.title || "New Session";
          let source = "";
          if (session.executed_queries && session.executed_queries.length > 0) {
            source = session.executed_queries[0].query;
          } else if (session.executed_query) {
            source = session.executed_query;
          }
          const hasUserMessages = session.messages && session.messages.length > 0 && session.messages.some(msg => msg.role === "user");
          return {
            id: session.id,
            author_id: session.role === "user" ? props.user.id : 0,
            summary: summary,
            update_time: session.update_time || Date.now(),
            source: source,
            message: session.content,
            type: session.content_type || "text",
            datatype: session.content_type === "table" ? "sql" : "text",
            description: session.description || "",
            hasUserMessages: hasUserMessages,
            is_pinned: session.is_pinned ?? false,
          };
        });
        setChatSessions((prev) => [...prev, ...newItems]);
        setSessionPage(nextPage);
        setTotalSessions((prev) => response.data.total ?? prev);
      }
    } catch (error) {
      console.error("Error loading more sessions:", error);
    } finally {
      setIsLoadingMoreSessions(false);
    }
  }, [isLoadingMoreSessions, chatSessions.length, totalSessions, sessionPage, SESSION_PAGE_SIZE, config.serviceUrl, config.agentId, config.apiKey, props.user.id]);

  useEffect(() => {
    // Only set active session on initial load when there's no active session
    if (chatSessions.length > 0 && !activeChatSession) {
      // Try to restore from localStorage (per agent)
      let restoredSessionId = null;
      if (config.agentId) {
        try {
          restoredSessionId = localStorage.getItem(getActiveSessionStorageKey(config.agentId));
        } catch (e) {
          // Ignore storage errors
        }
      }
      // Only use if it exists in the loaded sessions
      if (restoredSessionId && chatSessions.some(s => s.id === restoredSessionId)) {
        setActiveChatSession(restoredSessionId);
      } else {
        // Fallback to most recent session
        const mostRecentSession = [...chatSessions].sort((a, b) => b.update_time - a.update_time)[0];
        setActiveChatSession(mostRecentSession.id);
      }
    } else if (chatSessions.length === 0 && activeChatSession !== null) {
      // If no sessions exist, ensure activeChatSession is null for proper welcome screen display
      setActiveChatSession(null);
    }
  }, [chatSessions, activeChatSession, config.agentId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Defensive check: Don't attempt to load messages if there's no active session
        if (activeChatSession != null) {
          // Check if this is a newly created session
          if (newlyCreatedSessionsRef.current.has(activeChatSession)) {
            
            // Remove the flag after skipping the initial fetch
            newlyCreatedSessionsRef.current.delete(activeChatSession);
            
            setIsLoadingMessages(false);
            return;
          }
          
          // Check if this session is known to be empty (no user messages)
          // If so, skip the API call and just show the welcome screen
          if (emptySessionsRef.current.has(activeChatSession)) {
            setLiveChat([]);
            setIsLoadingMessages(false);
            return;
          }
          
          setIsLoadingMessages(true);
          // Clear messages immediately when switching sessions to prevent race conditions
          setLiveChat([]);

          const agentId = config.agentId;

          // Use the sessions endpoint with session ID filter
          const response = await axios.get(`${config.serviceUrl}/agents/${agentId}/sessions`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${config.apiKey}`,
            },
            params: {
              id: activeChatSession,
              discover_user_id: getUserIdDiscover(),
            },
          });

          if (response.status === 200 && response.data && response.data.data && response.data.data.length > 0) {
            // Get the first session that matches the ID
            const session = response.data.data[0];

            if (session.messages && Array.isArray(session.messages)) {
              
              // Get feedback from localStorage as fallback
              const feedbackCache = getFeedbackFromStorage();
              
              // Map the messages to the format expected by our components
              const formattedMessages = session.messages.map((msg) => {
                // Try to get feedback from API first, fallback to localStorage
                const apiFeedback = msg.feedback;
                const cachedFeedback = feedbackCache[msg.id];
                const finalFeedback = apiFeedback !== undefined ? apiFeedback : (cachedFeedback || 0);
              
                // Extract source (SQL query) from executed_queries or executed_query if available
                let source = "";
                let datatype = "text";
                let finalMessage = msg.content;
                let finalDescription = msg.description || "";

                // Handle both executed_queries (array) and executed_query (string)
                if (msg.executed_queries && msg.executed_queries.length > 0) {
                  source = msg.executed_queries;
                  datatype = "sql";
                } else if (msg.executed_query) {
                  source = msg.executed_query;
                  datatype = "sql";
                }

                // Fallback: content_type="table" always means SQL/table data
                if (datatype === "text" && msg.content_type === "table") {
                  datatype = "sql";
                }

                // Check if the message content is a JSON response that should be parsed
                let isSummaryResponse = false;
                if (msg.content && isJSONResponse(msg.content)) {
                  const processed = processJSONContent(
                    msg.content,
                    !!(msg.clarification_options && msg.clarification_options.length > 0)
                  );
                  if (processed) {
                    if (processed.datatype !== null) datatype = processed.datatype;
                    finalMessage = processed.finalMessage;
                    if (processed.finalDescription !== null) finalDescription = processed.finalDescription;
                    isSummaryResponse = processed.isSummary || false;
                  }
                } else if (datatype === "sql" && finalMessage) {
                  finalMessage = extractSqlTableData(finalMessage);
                }

                // For unknown answer_type: show only key_insights, skip recommendations with internal IDs
                if (msg.answer_type === 'unknown') {
                  const insightsText = extractUnknownTypeInsights(msg.content);
                  if (insightsText !== null) {
                    finalMessage = insightsText;
                    finalDescription = '';
                    datatype = 'text';
                  }
                }

                // Always check if finalMessage is an agent execution records array and extract the
                // nested answer. Runs unconditionally so it also catches cases where
                // executed_queries caused datatype="sql" but content is still a records array.
                if (finalMessage) {
                  const agentResult = processAgentExecutionAnswer(finalMessage);
                  if (agentResult !== null) {
                    finalMessage = agentResult.answer;
                    if (agentResult.isSql) {
                      datatype = "sql";
                    }
                  }
                }

                // Normalize timestamp – if it's in milliseconds (i.e., an unreasonably large value for seconds, > 10000000000 seconds ≈ year 2286), convert to seconds
                let timestamp = msg.created_at;
                if (timestamp != null) {
                  // If timestamp is larger than 10000000000, it's likely in milliseconds
                  if (timestamp > 10000000000) {
                    timestamp = Math.floor(timestamp / 1000);
                  }
                } else {
                  timestamp = Math.floor(Date.now() / 1000);
                }

                // Infer analysis_mode: fast-mode summary responses have answer_summary (not key_insights);
                // deep-mode responses have a non-empty description from key_insights/recommendations.
                const inferredMode = isSummaryResponse ? "fast" :
                  (finalDescription && finalDescription.trim() !== "") ? "deep" : "fast";

                return {
                  id: msg.id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                  author_id: msg.role === "user" ? props.user.id : 0,
                  message: finalMessage,
                  source: source,
                  type: msg.content_type || "text",
                  datatype: datatype,
                  description: finalDescription,
                  clarification_options: msg.clarification_options || null,
                  curl_commands: msg.curl_commands || [],
                  feedback: finalFeedback,
                  created_at: timestamp,
                  total_rows: msg.total_count || null,
                  data_sources_map: msg.answer?.data_sources || null,
                  reference: msg.reference || null,
                  analysis_mode: msg.role !== "user" ? inferredMode : undefined,
                  answer_type: msg.answer_type || null,
                  // Persisted reasoning-step timeline — without this the explicit
                  // allowlist would drop it on every history reload.
                  reasoning_steps: msg.reasoning_steps || null,
                };
              });

              setLiveChat(formattedMessages);

              // Update sessionModes from the last bot message's inferred mode — but only when no
              // explicit mode was already saved for this session (e.g. by handleSend). This prevents
              // a partial/mid-stream reload from overwriting "deep" with "fast" because the
              // description field hasn't arrived yet (empty description → inferred "fast").
              const lastBotMsg = [...formattedMessages].reverse().find(m => m.author_id === 0);
              if (lastBotMsg?.analysis_mode && activeChatSession) {
                const storedModes = loadSessionModes(config.agentId);
                if (!storedModes[activeChatSession]) {
                  setSessionModes((prev) => ({ ...prev, [activeChatSession]: lastBotMsg.analysis_mode }));
                  saveSessionMode(config.agentId, activeChatSession, lastBotMsg.analysis_mode);
                } else {
                  // Explicit mode already stored; just sync state without overwriting localStorage
                  setSessionModes((prev) => ({ ...prev, [activeChatSession]: storedModes[activeChatSession] }));
                }
              }

              // Check if session has user messages and update empty session tracking
              const hasUserMessages = formattedMessages.some(msg => 
                msg.author_id !== 0 && msg.author_id !== "0" && msg.author_id !== null
              );
              
              if (hasUserMessages) {
                // Session has user messages, remove from empty sessions tracking
                emptySessionsRef.current.delete(activeChatSession);
              } else {
                // Session has no user messages, add to empty sessions tracking
                emptySessionsRef.current.add(activeChatSession);
              }
            } else {
              // No messages array, this is an empty session
              setLiveChat([]);
              emptySessionsRef.current.add(activeChatSession);
            }
          } else {
            // Session exists but API returned empty data - treat as empty session
            setLiveChat([]);
            if (activeChatSession) {
              emptySessionsRef.current.add(activeChatSession);
            }
          }
        } else {
          // No active session, clear messages
          setLiveChat([]);
        }
      } catch (error) {
        console.error("Failed to load messages:", error);
      } finally {
        // Set loading to false when done, whether successful or not
        setIsLoadingMessages(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.agentId, config.serviceUrl, config.apiKey, activeChatSession, props.user.id]);

  const createNewSession = async () => {
    // Guard: cannot create session without a valid agent
    if (!config.agentId) return;

    // ChatGPT-like behavior: Check the LATEST session (most recent by update_time)
    // If the latest session has no user messages, switch to it instead of creating a new one
    // This prevents creating multiple empty sessions even when viewing an older session
    
    // Find the most recent session by update_time
    const mostRecentSession = chatSessions.length > 0 
      ? [...chatSessions].sort((a, b) => b.update_time - a.update_time)[0]
      : null;
    
    // If there's a most recent session, check if it has user messages
    if (mostRecentSession) {
      let latestSessionIsEmpty = false;
      
      // CORE FIX: Always check the actual state to determine if session is empty
      // If we're currently viewing the most recent session, use liveChat (which is always up-to-date)
      if (activeChatSession === mostRecentSession.id) {
        // Check for user messages in current liveChat
        const userMessages = liveChat.filter(msg => 
          msg.author_id !== 0 && msg.author_id !== "0" && msg.author_id !== null
        );
        latestSessionIsEmpty = userMessages.length === 0;
      } else {
        // Not viewing most recent session - need to fetch fresh data from server to ensure accuracy
        // The hasUserMessages flag in chatSessions might be stale if user sent message then switched sessions
        try {
          const agentId = config.agentId;
          const response = await axios.get(`${config.serviceUrl}/agents/${agentId}/sessions`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${config.apiKey}`,
            },
            params: {
              id: mostRecentSession.id,
              discover_user_id: getUserIdDiscover(),
            },
          });

          if (response.status === 200 && response.data && response.data.data && response.data.data.length > 0) {
            const session = response.data.data[0];
            // Check if session has any user messages
            const hasUserMessages = session.messages && session.messages.length > 0 && 
              session.messages.some(msg => msg.role === "user");
            latestSessionIsEmpty = !hasUserMessages;
            
            // Update our local cache with fresh data
            setChatSessions(prevSessions => 
              prevSessions.map(s => 
                s.id === mostRecentSession.id 
                  ? { ...s, hasUserMessages: hasUserMessages }
                  : s
              )
            );
          }
        } catch (error) {
          console.error("Error fetching latest session data:", error);
          // Fallback to cached flag if fetch fails
          latestSessionIsEmpty = mostRecentSession.hasUserMessages === false;
        }
      }
      
      // If latest session is empty (zero messages), navigate to it
      if (latestSessionIsEmpty) {
        setActiveChatSession(mostRecentSession.id);
        setSelectedScreen("ChatScreen");
        return;
      } else {
      }
    }

    // Create a new session if:
    // 1. There are no sessions at all, OR
    // 2. The most recent session has at least one user message
    try {
      // Make API call to create a new session
      const agentId = config.agentId;
      const sessionBody = {
        ...(securityParams || {}),
        discover_user_id: getUserIdDiscover(), // always wins — cannot be overridden by security params
      };
      const response = await axios.post(
        `${config.serviceUrl}/agents/${agentId}/sessions`,
        sessionBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${config.apiKey}`,
          },
        }
      );

      if (response.data && response.data.data) {
        const sessionId = response.data.data.id;

        // Add the new session to our list
        const newChatSession = {
          id: sessionId,
          summary: "New Session",
          update_time: Date.now(),
          hasUserMessages: false, // New sessions start empty
        };

        setChatSessions((prevSessions) => [...prevSessions, newChatSession]);

        // Mark this session as newly created to prevent message fetch
        newlyCreatedSessionsRef.current.add(sessionId);
        
        // Mark this session as empty (no user messages yet)
        emptySessionsRef.current.add(sessionId);

        // Set this as the active session and clear chat
        setActiveChatSession(sessionId);
        setLiveChat([]);

        // Ensure we're on the chat screen
        setSelectedScreen("ChatScreen");
      } else {
        // Backend returned HTTP 2xx but with an error code in the body
        // (e.g. {"code": 102, "message": "Missing required security parameter(s): ..."})
        const errMsg =
          response.data?.message ||
          "Failed to create session. Please try again.";
        throw new Error(errMsg);
      }
    } catch (error) {
      console.error("Error creating new session:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        error.message ||
        "Failed to create new session. Please try again.";
      // Extract the individual missing param names so the banner can name them.
      // The backend message looks like: "Missing required security parameter(s): client_id, user_id"
      const paramMatch = errMsg.match(/parameter\(?s?\)?:\s*(.+)/i);
      const names = paramMatch
        ? paramMatch[1].split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
        : [];
      if (names.length > 0) {
        // Genuinely missing params — show amber "not set" banner with the param names
        setMissingSessionParams(names);
        setSessionCreationError(null);
      } else {
        // Some other error (e.g. invalid characters) — show a general error banner
        setSessionCreationError(errMsg);
        setMissingSessionParams([]);
      }
      setSelectedScreen("ChatScreen");
    }
  };

  // Show loading while config is being fetched
  if (isConfigLoading || !config) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 dark:text-gray-400">Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Show error if config failed to load
  if (configError) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center max-w-md p-6 bg-white dark:bg-slate-800 rounded-lg shadow">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Configuration Error</h2>
          <p className="text-gray-600 dark:text-gray-400">{configError}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="content">
      {showDeleteModal && (
        <button
          className="position-fixed top-0 start-0 w-100 h-100 border-0 p-0 bg-transparent"
          style={{
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 1040,
            pointerEvents: "all",
            outline: "none",
            boxShadow: "none",
          }}
          aria-label="Close modal"
          onClick={(e) => setShowDeleteModal(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowDeleteModal(false);
            }
          }}
        ></button>
      )}

      <div className="h-100" style={{ padding: 0, margin: 0 }}>
        <NavBar 
          scrollDirection={scrollDirection} 
          isSideBar={isSideBar}
          user={props.user}
          handleLogout={props.handleLogout}
        />

        <div className="flex flex-nowrap h-full" style={{ margin: 0 }}>
          {/* Sidebar Component - fixed position */}
          <Sidebar
            isExpanded={isSideBar}
            onToggle={() => setIsSideBar(!isSideBar)}
            onNewChat={createNewSession}
            sessions={chatSessions}
            activeSessionId={activeChatSession}
            onSessionClick={setActiveChatSession}
            onSessionDelete={handleDeleteClick}
            onSessionPin={handleSessionPin}
            onSessionRename={handleSessionRename}
            isLoadingSessions={isLoadingSessions}
            setSelectedScreen={setSelectedScreen}
            onOpenIntegrations={() => setShowIntegrationsModal(true)}
            streamingSessions={streamingSessions}
            unreadSessions={unreadSessions}
            hasMoreSessions={chatSessions.length < totalSessions}
            isLoadingMoreSessions={isLoadingMoreSessions}
            loadMoreSessions={loadMoreSessions}
          />
          
          {/* Spacer for fixed sidebar */}
          <div 
            className="flex-shrink-0 transition-all duration-300"
            style={{ width: isSideBar ? '260px' : '56px' }}
          />

          <div className="p-0 transition-width flex-1" style={{ minWidth: 0 }}>
            {selectedScreen === "ChatScreen" &&
              (isLoadingSessions ? (
                // Show a full-page loading spinner while sessions are loading
                <div className="flex justify-center items-center" style={{ height: "80vh" }}>
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-3 text-gray-500 dark:text-gray-400">Loading your conversations...</p>
                  </div>
                </div>
              ) : (
                // Show the ChatScreen component once sessions are loaded
                <ChatScreen
                  liveChat={liveChat}
                  user={props.user}
                  handleLogout={props.handleLogout}
                  handleScroll={handleScroll}
                  isSideBar={!!isSideBar}
                  activeChatSession={activeChatSession}
                  setActiveChatSession={setActiveChatSession}
                  chatSessions={chatSessions}
                  setChatSessions={setChatSessions}
                  setLiveChat={setLiveChat}
                  updateSessionTimestamp={updateSessionTimestamp}
                  isLoadingMessages={isLoadingMessages}
                  AgentSelector={AgentSelector}
                  newlyCreatedSessionsRef={newlyCreatedSessionsRef}
                  emptySessionsRef={emptySessionsRef}
                  onSessionModeChange={handleSessionModeChange}
                  sessionModes={sessionModes}
                  onStreamStart={handleStreamStart}
                  onStreamEnd={handleStreamEnd}
                  missingSessionParams={missingSessionParams}
                  sessionCreationError={sessionCreationError}
                />
              ))}

            {selectedScreen === "AdminDataSourcesScreen" && <AdminDataSourcesScreen />}

            {selectedScreen === "AdminUserScreen" && <AdminUserScreen />}
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Chat?"
        message="Are you sure you want to delete this chat? This chat can't be recovered."
        onConfirm={deleteSession}
        onCancel={() => setShowDeleteModal(false)}
        isLoading={isDeleting}
      />

      <SessionNotification
        notifications={sessionNotifications}
        onDismiss={removeSessionNotification}
      />

      {/* Integrations Modal */}
      <IntegrationsModal
        isOpen={showIntegrationsModal}
        onClose={() => setShowIntegrationsModal(false)}
      />
    </main>
  );
}

MainContent.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  handleLogout: PropTypes.func.isRequired,
};

export default MainContent;

